import { html, View } from 'rune-ts';
import { createCreatorProductStore } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/createStore.ts';
import { formKeyMap } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import { Input_NumberEditor } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_NumberEditor.ts';
import { BaseSection } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/BaseSection.ts';
import { Input_Date } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Date.ts';
import { ChangeCalendarEvent } from '@services/MarppleShop/marppleShopStudioApp/components/cells/StudioCalendar/StudioCalendar.ts';
import {
  DeleteDateEvent,
  selectCheckBoxEvent,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/events.ts';
import { each, entries, filter, pipe } from '@fxts/core';
import { addMinutes, subMinutes } from 'date-fns';
import { MAX_PURCHASE_PER_USER__MAX } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/constants.ts';
import { formatDateField } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/helper.ts';
import { Input_CheckBoxes } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_CheckBoxes.ts';
import { MODE } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import klass from './styles/badge.module.scss';
import color_klass from './styles/color.module.scss';
import { classes } from '@services/MarppleShop/shared/util';
import { isNessApp } from '@modules/Util/S/Function/service_app_name';
import { font } from '@services/MarppleShop/shared/typography';

interface SalesInfoSectionData<T extends MODE> {
  store: ReturnType<typeof createCreatorProductStore<T>>;
}

const BADGE_ID_MAP = {
  LIMIT: 1,
};

export class SalesInfoSection<T extends MODE> extends BaseSection<T, SalesInfoSectionData<T>> {
  protected readonly sectionTitle = '판매 정보';
  private store = this.data.store;
  private inputMap = {
    [formKeyMap.is_force_out_of_stock]: new Input_CheckBoxes({
      name: formKeyMap.is_force_out_of_stock,
      value: [{ id: 1, label: '품절 상태로 표시합니다.', active: this.store.formData.is_force_out_of_stock }],
      title: '매진 설정',
    }),
    [formKeyMap.sell_start_at]: new Input_Date({
      name: formKeyMap.sell_start_at,
      value: formatDateField(this.store.formData.sell_start_at),
      placeholder: '즉시 판매',
      title: '판매 시작일시',
    }),
    [formKeyMap.sell_end_at]: new Input_Date({
      name: formKeyMap.sell_end_at,
      value: formatDateField(this.store.formData.sell_end_at),
      placeholder: '상시 판매',
      title: '판매 종료일시',
    }),
    [formKeyMap.max_purchase_per_user]: new Input_NumberEditor({
      name: formKeyMap.product_name,
      value: this.store.formData.max_purchase_per_user ?? null,
      title: '인당 최대구매수량',
      option: {
        placeholder: '한 계정으로 구매할 수 있는 최대 수량을 입력해 주세요.',
        max: MAX_PURCHASE_PER_USER__MAX,
        right_html: html`<div class="${color_klass.gray_80} ${font('14_medium')}">개</div>`,
      },
    }),
    /* 현재는 한정만 존재하지만 추후 여러 뱃지가 추가될 수 있다. */
    [formKeyMap.meta]: new Input_CheckBoxes({
      name: formKeyMap.meta,
      value: [
        {
          id: BADGE_ID_MAP.LIMIT,
          label: html`<div class="${klass.badge_label_wrap}">
            <div
              class="${classes(klass.badge, {
                [klass.ness]: isNessApp(),
              })}"
            >
              ${isNessApp() ? 'Special Offer' : '한정'}
            </div>
            <div class="${font('14_medium')}">뱃지를 표시합니다.</div>
          </div>`,
          active: this.store.formData.meta.limited,
        },
      ],
      title: '뱃지 설정',
    }),
  };

  protected override inputViews(): Array<View> {
    return Object.values(this.inputMap);
  }

  protected override onRender() {
    this.setDateViewEvent();
    this.setDateViewSubscribe();
  }

  private setDateViewEvent() {
    pipe(
      this.inputMap,
      entries,
      filter(
        (entry): entry is [typeof formKeyMap.sell_start_at | typeof formKeyMap.sell_end_at, Input_Date] => {
          const [key] = entry;
          return key === formKeyMap.sell_start_at || key === formKeyMap.sell_end_at;
        },
      ),
      each(([name, view]) => {
        view
          .addEventListener(ChangeCalendarEvent, (e) => {
            this.store.actions.setField(name, e.detail.start_date);
          })
          .addEventListener(DeleteDateEvent, () => {
            this.store.actions.setField(name, null);
          });
      }),
    );

    this.addEventListener(selectCheckBoxEvent, (e) => {
      switch (e.detail.name) {
        case formKeyMap.meta: {
          this.store.actions.setField(
            formKeyMap.limited,
            !!e.detail.checkbox_ids.find((id) => id === BADGE_ID_MAP.LIMIT),
          );
          break;
        }
        case formKeyMap.is_force_out_of_stock: {
          this.store.actions.setField(formKeyMap.is_force_out_of_stock, !!e.detail.checkbox_ids?.length);
          break;
        }
      }
    });
  }

  private setDateViewSubscribe() {
    this.store.subscribeState(
      (state) => state.formData.sell_start_at,
      (sell_start_at) => {
        this.inputMap.sell_start_at.setDate(sell_start_at);
        // 시작일에서 1분을 더한 값을 시작일의 최대값으로 설정
        const minEndDate = sell_start_at ? addMinutes(sell_start_at, 1) : sell_start_at;
        this.inputMap.sell_end_at.setMinDate(minEndDate);
      },
    );

    this.store.subscribeState(
      (state) => state.formData.sell_end_at,
      (sell_end_at) => {
        this.inputMap.sell_end_at.setDate(sell_end_at);
        // 종료일에서 1분을 뺀 값을 시작일의 최대값으로 설정
        const maxStartDate = sell_end_at ? subMinutes(sell_end_at, 1) : sell_end_at;
        this.inputMap.sell_start_at.setMaxDate(maxStartDate);
      },
    );
  }
}
